import type {
  TrackingEvent,
  InteractionTrackingEvent,
} from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';

export type TrackingType = {
  PropTrackAction?: string;
  PropActionOrigin?: string;
  PropChannel?: string;
  pageName?: string;
};

export type PageView = {
  channel?: string;
  page: string;
  PropContextDimension1?: string;
  PropTrackAction?: string;
};

export type XingTrackType =
  | TrackingEvent<PageView>
  | InteractionTrackingEvent<TrackingType>;

export type UsePageView = {
  track: XingTrackType;
  page: string;
  PropContextDimension1?: string;
  PropTrackAction?: string;
};

export function useTrackPageView(): () => void {
  const { track } = useTracking<XingTrackType>();

  return () => {
    track({
      type: 'pageview',
      page: 'wbm/Welcome/start/index',
      channel: 'wbm/Welcome',
      PropContextDimension1: 'welcome_lo_startpage_signup_view',
    });

    track({
      type: 'nwt',
      eventSchema: 'basic',
      sentBy: 'welcome',
      page: 'welcome/start/index',
      event: 'viewed_screen',
      channel: 'wbm/Welcome',
    });
  };
}

export function useTrackClickStoreButton(): (action: string) => void {
  const { track } = useTracking<XingTrackType>();

  return (action: string) => {
    const mobileOs = action.includes('android') ? 'android' : 'ios';

    track({
      event: 'PropTrackAction',
      type: 'interaction',
      PropTrackAction: action,
      channel: undefined,
      PropContextDimension1: undefined,
      PropChannel: undefined,
    });

    track({
      event: 'visited',
      eventSchema: 'basic',
      sentBy: 'welcome',
      element: `download_${mobileOs}_app`,
      page: 'welcome/start/index',
      type: 'nwt',
      channel: 'wbm/Welcome',
    });
  };
}

export function useTrackSignUpGoogle(): () => void {
  const { track } = useTracking<XingTrackType>();

  return () => {
    track({
      event: 'PropTrackAction',
      pageName: 'wbm/Welcome/start/index/google',
      type: 'interaction',
      PropTrackAction: 'lp_google_click',
      channel: undefined,
      PropContextDimension1: undefined,
      PropChannel: 'wbm/Welcome',
    });

    track({
      event: 'visited',
      eventSchema: 'basic',
      sentBy: 'welcome',
      element: 'continue_with_google',
      page: 'welcome/start/index',
      type: 'nwt',
      channel: 'wbm/Welcome',
    });
  };
}

export function useTrackSignUp(): () => void {
  const { track } = useTracking<XingTrackType>();

  return () =>
    track({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'welcome',
      element: 'register',
      elementDetail: 'create_profile_button',
      page: 'welcome/start/index',
      channel: 'wbm/Welcome',
    });
}

export function useTrackClickSerpCategory(): (categoryName: string) => void {
  const { track } = useTracking<XingTrackType>();

  return (categoryName: string) =>
    track({
      event: 'PropTrackAction',
      type: 'interaction',
      PropTrackAction: `losp_serp_category_${categoryName}_header_click`,
    });
}

// LOSP PREFERENCES EXPERIMENT

export function useTrackClickFenced(): (item: string) => void {
  const { track } = useTracking<XingTrackType>();

  return (item: string) =>
    track({
      event: 'PropTrackAction',
      type: 'interaction',
      PropTrackAction: `losp_preferences_fenced_${item}_click`,
    });
}

export function useTrackSignupExperimentClick(): () => void {
  const { track } = useTracking<XingTrackType>();

  return () =>
    track({
      event: 'PropTrackAction',
      type: 'interaction',
      PropTrackAction: `losp_preferences_modal_sign_up_button_click`,
    });
}

export function useTrackSaveClick(): (isEmpty: boolean) => void {
  const { track } = useTracking<XingTrackType>();

  return (isEmpty: boolean) => {
    track({
      event: 'PropTrackAction',
      type: 'interaction',
      PropTrackAction: 'losp_preferences_save_click',
      PropActionOrigin: isEmpty
        ? 'losp_preferences_not_selected'
        : 'losp_preferences_selected',
    });

    track({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'welcome',
      element: 'save_job_preferences',
      page: 'welcome/start/index',
      channel: 'wbm/Welcome',
    });
  };
}
